import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { type WorkQueueBucket, type WorkQueueItem } from '@aps/contracts';
import {
  Button,
  DataTable,
  EmptyState,
  Skeleton,
  formatDate,
  formatMoney,
  type DataTableColumn,
} from '@aps/ui';
import { ApiError, api } from '../lib/api.js';
import { bucketLabel, messages, waitingText } from '../i18n/messages.js';
import { useSession } from '../app/session-context.js';
import { QueueError } from './WorkQueueScreen.js';

interface BucketScreenProps {
  bucket: WorkQueueBucket;
  onBack: () => void;
}

const PAGE_SIZE = 25;

/**
 * Bitta guruh ichidagi vazifalar.
 *
 * Tartib serverda: eng uzoq kutgani birinchi. Klient qayta saralamaydi —
 * aks holda sahifalash bilan ziddiyat chiqadi.
 */
export function BucketScreen({ bucket, onBack }: BucketScreenProps): ReactNode {
  const { locale } = useSession();
  const t = messages(locale);
  const [cursors, setCursors] = useState<string[]>([]);

  const cursor = cursors.at(-1);
  const page = useQuery({
    queryKey: ['work-queue', bucket, cursor ?? null],
    queryFn: () =>
      api.workQueueItems(bucket, { limit: PAGE_SIZE, ...(cursor === undefined ? {} : { cursor }) }),
  });

  const title = bucketLabel(bucket, locale);

  const header = (
    <header className="adm-page__header">
      <h1 className="adm-page__title">{title}</h1>
      <Button variant="ghost" size="sm" onClick={onBack}>
        {t.back}
      </Button>
    </header>
  );

  if (page.isPending) {
    return (
      <section className="adm-page">
        {header}
        <Skeleton height="220px" label={t.loading} />
      </section>
    );
  }

  if (page.isError) {
    return (
      <section className="adm-page">
        {header}
        <QueueError
          message={page.error instanceof ApiError ? page.error.text(locale) : t.errorTitle}
          retryLabel={t.retry}
          onRetry={() => void page.refetch()}
        />
      </section>
    );
  }

  const now = Date.now();
  const columns: DataTableColumn<WorkQueueItem>[] = [
    {
      key: 'order',
      header: t.colOrder,
      render: (row) => (
        <span className="adm-cell-strong">
          {row.orderNumber}
          <br />
          <span className="aps-muted">{t.itemsCount(row.itemCount)}</span>
        </span>
      ),
    },
    {
      key: 'customer',
      header: t.colCustomer,
      render: (row) => (
        <span>
          {row.customerName}
          <br />
          <span className="aps-muted">{row.customerPhone}</span>
        </span>
      ),
    },
    {
      key: 'shop',
      header: t.colShop,
      // Do'kon tanlanmagani MUHIM ma'lumot: SHOP oqimida tasdiqlashdan
      // oldin do'kon tanlanishi shart, aks holda qoldiq rezervlanmaydi.
      render: (row) => row.shopNameUz ?? <span className="adm-cell-danger">{t.shopUnset}</span>,
    },
    {
      key: 'status',
      header: t.colStatus,
      render: (row) =>
        locale === 'RU' ? (row.statusLabelRu ?? row.statusLabelUz) : row.statusLabelUz,
    },
    {
      key: 'total',
      header: t.colTotal,
      numeric: true,
      render: (row) => formatMoney(row.orderTotal, locale),
    },
    {
      key: 'waiting',
      header: t.colWaiting,
      numeric: true,
      render: (row) => waitingText(row.waitingSince, now, locale),
    },
    {
      key: 'eta',
      header: t.colEta,
      render: (row) => {
        if (row.etaTo === null) return <span className="aps-muted">{t.etaUnset}</span>;
        const text = formatDate(row.etaTo, locale);
        return row.isOverdue ? <span className="adm-cell-danger">{text}</span> : text;
      },
    },
  ];

  return (
    <section className="adm-page">
      {header}

      <DataTable
        caption={title}
        columns={columns}
        rows={page.data.items}
        rowKey={(row) => row.shipmentId}
        empty={<EmptyState title={t.bucketEmpty} />}
      />

      {page.data.nextCursor !== null && (
        <div className="adm-table-footer">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const next = page.data.nextCursor;
              if (next !== null) setCursors((current) => [...current, next]);
            }}
          >
            {t.loadMore}
          </Button>
        </div>
      )}
    </section>
  );
}
